import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { usersApi, tasksApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import TaskStageBadge from "../components/TaskStageBadge";
import { isTaskDone, stageBadgeStyle } from "../lib/taskStage";
import { Search, Star, Briefcase, CheckCircle2, Clock, AlertTriangle, Edit3, Save, UserPlus, Pencil, Eye, User, FileCheck, DollarSign, Users, Plus, Trash2, Settings2, ChevronDown, Send } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

// Specialization config
const SPECIALIZATIONS = [
  { value: "exterior", label: "Exterior", color: "#3b82f6" },
  { value: "interior", label: "Interior", color: "#8b5cf6" },
  { value: "aerial", label: "Aerial", color: "#06b6d4" },
  { value: "animation", label: "Animation", color: "#f59e0b" },
  { value: "floor_plan", label: "Floor Plan", color: "#10b981" },
  { value: "modeller", label: "Modeller", color: "#ec4899" },
  { value: "other", label: "Other", color: "#6b7280" },
];

const ArtistsPage = () => {
  const { user } = useAuth();
  const [artists, setArtists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("rating");
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [specializationFilter, setSpecializationFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [teams, setTeams] = useState([]);
  
  // Artist tasks dialog
  const [showTasksDialog, setShowTasksDialog] = useState(false);
  const [selectedArtistTasks, setSelectedArtistTasks] = useState([]);
  const [selectedArtistName, setSelectedArtistName] = useState("");
  const [selectedArtistId, setSelectedArtistId] = useState(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [tasksTab, setTasksTab] = useState("active"); // "active" or "completed"
  
  // New/Edit artist dialog
  const [showNewArtist, setShowNewArtist] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [newArtist, setNewArtist] = useState({
    name: "",
    email: "",
    password: "123456", // Default password
    specializations: [],
    rating: 7,
    phone: "",
    telegram: "",
    portfolio_link: "",
    notes: "",
    hourly_rate: 0,
    currency: "UAH",
  });

  // Check if user can edit artists
  const canEditArtists = ["admin", "pm_lead", "team", "art_director"].includes(user?.role);

  // Team management
  const [showTeamsDialog, setShowTeamsDialog] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamColor, setNewTeamColor] = useState("#f59e0b");
  const [creatingTeam, setCreatingTeam] = useState(false);

  const TEAM_COLORS = [
    "#f59e0b", "#ef4444", "#3b82f6", "#10b981", "#8b5cf6",
    "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
  ];

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setCreatingTeam(true);
    try {
      const res = await api.post("/settings/teams", {
        name: newTeamName.trim(),
        color: newTeamColor,
      });
      setTeams(prev => [...prev, res.data]);
      setNewTeamName("");
      toast.success(`Команда "${newTeamName.trim()}" создана`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания команды");
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Удалить команду "${teamName}"? Артисты будут откреплены.`)) return;
    try {
      await api.delete(`/settings/teams/${teamId}`);
      setTeams(prev => prev.filter(t => t.id !== teamId));
      setArtists(prev => prev.map(a => a.team === teamId ? { ...a, team: null } : a));
      toast.success(`Команда "${teamName}" удалена`);
    } catch {
      toast.error("Ошибка удаления команды");
    }
  };

  const handleQuickTeamAssign = async (artistId, teamId) => {
    const teamVal = teamId === "none" ? null : teamId;
    try {
      await api.put(`/users/${artistId}/team`, { team: teamVal });
      setArtists(prev => prev.map(a => a.id === artistId ? { ...a, team: teamVal } : a));
      const teamName = teamVal ? teams.find(t => t.id === teamVal)?.name : "без команды";
      toast.success(`Команда обновлена: ${teamName}`);
    } catch {
      toast.error("Ошибка обновления команды");
    }
  };

  const resetArtistForm = () => {
    setEditingArtist(null);
    setNewArtist({
      name: "",
      email: "",
      password: "123456",
      specializations: [],
      rating: 7,
      phone: "",
      telegram: "",
      portfolio_link: "",
      notes: "",
      hourly_rate: 0,
      currency: "UAH",
    });
  };

  const handleEditArtist = (artist) => {
    setEditingArtist(artist);
    setNewArtist({
      name: artist.name,
      email: artist.email,
      password: "",
      specializations: artist.specializations || [],
      rating: artist.rating || 7,
      phone: artist.phone || "",
      telegram: artist.telegram || "",
      portfolio_link: artist.portfolio_link || "",
      notes: artist.notes || "",
      hourly_rate: artist.hourly_rate || 0,
      currency: artist.currency || "UAH",
    });
    setShowNewArtist(true);
  };

  const handleShowArtistTasks = async (artist) => {
    setSelectedArtistName(artist.name);
    setSelectedArtistId(artist.id);
    setShowTasksDialog(true);
    setLoadingTasks(true);
    setTasksTab("active"); // Default to active tab
    try {
      const res = await api.get(`/tasks?assigned_artist_id=${artist.id}`);
      // Filter to only this artist's tasks (double-check frontend)
      const artistTasks = (res.data || []).filter(t => t.assigned_artist_id === artist.id);
      setSelectedArtistTasks(artistTasks);
    } catch (error) {
      console.error("Error fetching artist tasks:", error);
      toast.error("Помилка завантаження тасок");
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [artistsRes, tasksRes, teamsRes] = await Promise.all([
        usersApi.getAll("artist"),
        tasksApi.getAll(),
        api.get("/settings/teams"),
      ]);
      
      // Calculate workload for each artist
      const artistsWithWorkload = artistsRes.data.map(artist => {
        const artistTasks = tasksRes.data.filter(t => t.assigned_artist_id === artist.id);
        // Active = not on the last stage of its timeline (влияет на загрузку)
        const activeTasks = artistTasks.filter(t => !isTaskDone(t));
        // Completed = on the last stage (для рейтингу)
        const completedTasks = artistTasks.filter(t => isTaskDone(t));
        const assignedNotClosed = activeTasks;
        
        // Use rating from database, default to 5 if not set
        const rating = artist.rating ?? 5;
        
        // Calculate workload percentage (max 5 active tasks = 100%)
        const workloadPercent = Math.min((activeTasks.length / 5) * 100, 100);
        
        return {
          ...artist,
          rating,
          activeTasks: activeTasks.length,
          completedTasks: completedTasks.length,
          assignedNotClosed: assignedNotClosed.length,  // NEW: all assigned not closed
          totalTasks: artistTasks.length,
          workloadPercent,
          isOverloaded: activeTasks.length >= 4,
          isFree: activeTasks.length === 0,
        };
      });
      
      setArtists(artistsWithWorkload);
      setTasks(tasksRes.data);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error("Error fetching artists:", error);
      toast.error("Failed to load artists");
    } finally {
      setLoading(false);
    }
  };

  const openSpecEditor = (artist) => {
    setEditingArtist(artist);
    setSelectedSpecs(artist.specializations || []);
  };

  const toggleSpec = (spec) => {
    setSelectedSpecs(prev => 
      prev.includes(spec) 
        ? prev.filter(s => s !== spec)
        : [...prev, spec]
    );
  };

  const saveSpecializations = async () => {
    if (!editingArtist) return;
    try {
      await api.put(`/users/${editingArtist.id}/specializations`, selectedSpecs);
      toast.success("Specializations updated");
      setEditingArtist(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update specializations");
    }
  };

  // Create or update artist
  const handleCreateArtist = async () => {
    if (!newArtist.name || !newArtist.email) {
      toast.error("Name and email are required");
      return;
    }
    
    try {
      if (editingArtist) {
        // Update existing artist
        const updateData = {
          name: newArtist.name,
          email: newArtist.email,
          phone: newArtist.phone,
          telegram: newArtist.telegram,
          portfolio_link: newArtist.portfolio_link,
          notes: newArtist.notes,
          hourly_rate: newArtist.hourly_rate,
          currency: newArtist.currency,
          specializations: newArtist.specializations,
          rating: newArtist.rating,
        };
        
        // Only include password if it was changed
        if (newArtist.password) {
          updateData.password = newArtist.password;
        }
        
        await usersApi.update(editingArtist.id, updateData);
        toast.success(`Artist "${newArtist.name}" updated`);
      } else {
        // Create new artist
        await api.post("/auth/register", {
          name: newArtist.name,
          email: newArtist.email,
          password: newArtist.password || "123456",
          role: "artist",
          phone: newArtist.phone,
          telegram: newArtist.telegram,
          portfolio_link: newArtist.portfolio_link,
          notes: newArtist.notes,
          hourly_rate: newArtist.hourly_rate,
          currency: newArtist.currency,
          specializations: newArtist.specializations,
          rating: newArtist.rating,
        });
        toast.success(`Artist "${newArtist.name}" created`);
      }
      
      setShowNewArtist(false);
      resetArtistForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save artist");
    }
  };

  const getAvailabilityBadge = (artist) => {
    if (artist.isOverloaded) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Overloaded
        </Badge>
      );
    }
    if (artist.isFree) {
      return (
        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Available
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20">
        <Clock className="h-3 w-3 mr-1" />
        Working
      </Badge>
    );
  };

  const getRatingStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        <Star className={cn("h-4 w-4", rating >= 8 ? "text-yellow-400 fill-yellow-400" : "text-zinc-600")} />
        <span className={cn("font-mono text-sm font-medium", rating >= 8 ? "text-yellow-400" : "text-zinc-400")}>
          {rating}/10
        </span>
      </div>
    );
  };

  const getWorkloadBar = (percent) => {
    let colorClass = "bg-green-500";
    if (percent >= 80) colorClass = "bg-red-500";
    else if (percent >= 50) colorClass = "bg-orange-500";
    else if (percent >= 25) colorClass = "bg-sky-500";
    
    return (
      <div className="w-24">
        <Progress value={percent} className="h-1.5 bg-zinc-800" indicatorClassName={colorClass} />
      </div>
    );
  };

  // Filter and sort artists
  let filteredArtists = artists.filter((a) => {
    const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAvailability = 
      availabilityFilter === "all" ||
      (availabilityFilter === "free" && a.isFree) ||
      (availabilityFilter === "working" && !a.isFree && !a.isOverloaded) ||
      (availabilityFilter === "overloaded" && a.isOverloaded);
    const matchesSpecialization = 
      specializationFilter === "all" ||
      (a.specializations || []).includes(specializationFilter);
    const matchesTeam = 
      teamFilter === "all" ||
      (teamFilter === "unassigned" ? !a.team : a.team === teamFilter);
    return matchesSearch && matchesAvailability && matchesSpecialization && matchesTeam;
  });

  // Sort
  filteredArtists = filteredArtists.sort((a, b) => {
    if (sortBy === "rating") return b.rating - a.rating;
    if (sortBy === "workload") return a.activeTasks - b.activeTasks;
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "specialization") {
      const aSpec = (a.specializations || [])[0] || "zzz";
      const bSpec = (b.specializations || [])[0] || "zzz";
      return aSpec.localeCompare(bSpec);
    }
    if (sortBy === "team") {
      const aTeam = a.team || "zzz";
      const bTeam = b.team || "zzz";
      return aTeam.localeCompare(bTeam);
    }
    return 0;
  });

  // Stats
  const totalArtists = artists.length;
  const freeArtists = artists.filter(a => a.isFree).length;
  const overloadedArtists = artists.filter(a => a.isOverloaded).length;
  const avgRating = artists.length > 0 ? (artists.reduce((sum, a) => sum + a.rating, 0) / artists.length).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="artists-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Artists</h1>
          <p className="text-sm text-zinc-500">Manage 3D designers and their workload</p>
        </div>
        {canEditArtists && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowTeamsDialog(true)}
              className="border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500"
              data-testid="manage-teams-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              Команды ({teams.length})
            </Button>
            <Button onClick={() => setShowNewArtist(true)} className="bg-orange-500 hover:bg-orange-600">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Artist
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Total Artists</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1">{totalArtists}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Available</p>
            <p className="text-2xl font-bold text-green-400 mt-1">{freeArtists}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Overloaded</p>
            <p className="text-2xl font-bold text-red-400 mt-1">{overloadedArtists}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Avg Rating</p>
            <p className="text-2xl font-bold text-yellow-400 mt-1">{avgRating}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 h-9"
            data-testid="search-artists"
          />
        </div>
        <Select value={specializationFilter} onValueChange={setSpecializationFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Specialization" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Specs</SelectItem>
            {SPECIALIZATIONS.map(spec => (
              <SelectItem key={spec.value} value={spec.value} className="text-zinc-300">
                {spec.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-zinc-50 h-9" data-testid="team-filter">
            <SelectValue placeholder="Team" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Teams</SelectItem>
            <SelectItem value="unassigned" className="text-zinc-300">No Team</SelectItem>
            {teams.map(t => (
              <SelectItem key={t.id} value={t.id} className="text-zinc-300">{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Availability" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All</SelectItem>
            <SelectItem value="free" className="text-zinc-300">Available</SelectItem>
            <SelectItem value="working" className="text-zinc-300">Working</SelectItem>
            <SelectItem value="overloaded" className="text-zinc-300">Overloaded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="rating" className="text-zinc-300">By Rating</SelectItem>
            <SelectItem value="workload" className="text-zinc-300">By Workload</SelectItem>
            <SelectItem value="name" className="text-zinc-300">By Name</SelectItem>
            <SelectItem value="specialization" className="text-zinc-300">By Specialization</SelectItem>
            <SelectItem value="team" className="text-zinc-300">By Team</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Specializations Editor Modal - Compact */}
      <Dialog open={!!editingArtist} onOpenChange={(open) => !open && setEditingArtist(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-sm p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-zinc-50 text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs">
                {editingArtist?.name?.charAt(0)}
              </span>
              {editingArtist?.name}
            </DialogTitle>
          </DialogHeader>
          {editingArtist && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {SPECIALIZATIONS.map((spec) => (
                  <div
                    key={spec.value}
                    onClick={() => toggleSpec(spec.value)}
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer border text-xs font-medium transition-all",
                      selectedSpecs.includes(spec.value)
                        ? "border-orange-500 bg-orange-500/10"
                        : "border-zinc-700 bg-zinc-800/50 hover:border-zinc-600"
                    )}
                    data-testid={`spec-option-${spec.value}`}
                    style={{ color: selectedSpecs.includes(spec.value) ? spec.color : '#a1a1aa' }}
                  >
                    <Checkbox
                      checked={selectedSpecs.includes(spec.value)}
                      className="h-3 w-3 border-zinc-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    {spec.label}
                  </div>
                ))}
              </div>
              
              <div className="pt-2 border-t border-zinc-800">
                <label className="text-xs text-zinc-400 mb-1.5 flex items-center gap-1.5">
                  <Users className="h-3 w-3" />Team
                </label>
                <Select
                  value={editingArtist.team || "none"}
                  onValueChange={async (val) => {
                    const teamVal = val === "none" ? null : val;
                    try {
                      await api.put(`/users/${editingArtist.id}/team`, { team: teamVal });
                      setArtists(prev => prev.map(a => a.id === editingArtist.id ? { ...a, team: teamVal } : a));
                      setEditingArtist(prev => ({ ...prev, team: teamVal }));
                      toast.success("Team updated");
                    } catch { toast.error("Failed to update team"); }
                  }}
                >
                  <SelectTrigger className="h-8 bg-zinc-800 border-zinc-700 text-zinc-50 text-xs">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="none" className="text-zinc-400 text-xs">No team</SelectItem>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id} className="text-zinc-300 text-xs">{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingArtist(null)}
                  className="text-zinc-400 hover:text-zinc-50 h-8"
                  data-testid="cancel-specializations"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveSpecializations}
                  className="bg-orange-500 hover:bg-orange-600 text-white h-8"
                  data-testid="save-specializations"
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Artists Table */}
      <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400 font-medium">Artist</TableHead>
              <TableHead className="text-zinc-400 font-medium">Specializations</TableHead>
              <TableHead className="text-zinc-400 font-medium">Team</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Rating</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Assigned</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Active</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Completed</TableHead>
              <TableHead className="text-zinc-400 font-medium">Workload</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
              {canEditArtists && <TableHead className="text-zinc-400 font-medium w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredArtists.map((artist) => (
              <TableRow
                key={artist.id}
                className="border-zinc-800 hover:bg-zinc-800/50"
                data-testid={`artist-row-${artist.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-zinc-300">
                        {artist.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-zinc-50 font-medium text-sm">{artist.name}</p>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <FileCheck className={cn("h-3.5 w-3.5", artist.contract_accepted ? "text-emerald-400" : "text-zinc-600")} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-xs">
                              {artist.contract_accepted ? "Contract signed" : "No contract"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <DollarSign className={cn("h-3.5 w-3.5", artist.desired_rates && Object.keys(artist.desired_rates).length > 0 ? "text-blue-400" : "text-zinc-600")} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-xs max-w-xs">
                              {artist.desired_rates && Object.keys(artist.desired_rates).length > 0 ? (
                                <div className="space-y-0.5">
                                  <p className="font-medium text-zinc-300 mb-1">{Object.keys(artist.desired_rates).length} services</p>
                                  {Object.entries(artist.desired_rates).map(([service, price]) => (
                                    <div key={service} className="flex justify-between gap-4 text-[11px]">
                                      <span className="text-zinc-400">{service}</span>
                                      <span className="text-green-400 font-mono">${price}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-zinc-500">No rates set</span>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <Send
                                  className={cn(
                                    "h-3.5 w-3.5",
                                    artist.telegram_connected ? "text-sky-400" : "text-zinc-600"
                                  )}
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 text-xs">
                              {artist.telegram_connected
                                ? `Telegram connected${artist.telegram ? ` (${artist.telegram})` : ""}`
                                : "Telegram not connected"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-xs text-zinc-500">{artist.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 flex-wrap">
                    {(artist.specializations || []).map(spec => {
                      const specConfig = SPECIALIZATIONS.find(s => s.value === spec);
                      return (
                        <span
                          key={spec}
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: `${specConfig?.color}25`,
                            color: specConfig?.color,
                            border: `1px solid ${specConfig?.color}50`
                          }}
                        >
                          {specConfig?.label || spec}
                        </span>
                      );
                    })}
                    {canEditArtists && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openSpecEditor(artist)}
                        className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-50"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {canEditArtists ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="flex items-center gap-1 group cursor-pointer hover:bg-zinc-800/50 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
                          data-testid={`team-assign-${artist.id}`}
                        >
                          {artist.team ? (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{
                                backgroundColor: `${teams.find(t => t.id === artist.team)?.color || "#6b7280"}20`,
                                color: teams.find(t => t.id === artist.team)?.color || "#6b7280",
                                border: `1px solid ${teams.find(t => t.id === artist.team)?.color || "#6b7280"}50`
                              }}
                            >
                              {teams.find(t => t.id === artist.team)?.name || artist.team}
                            </span>
                          ) : (
                            <span className="text-zinc-600 text-xs">— без команды</span>
                          )}
                          <ChevronDown className="h-3 w-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-1 bg-zinc-900 border-zinc-700" align="start">
                        <div className="space-y-0.5">
                          <button
                            onClick={() => handleQuickTeamAssign(artist.id, "none")}
                            className={cn(
                              "w-full text-left text-xs px-2 py-1.5 rounded hover:bg-zinc-800 transition-colors",
                              !artist.team ? "text-orange-400 bg-zinc-800/50" : "text-zinc-400"
                            )}
                            data-testid={`team-option-none-${artist.id}`}
                          >
                            Без команды
                          </button>
                          {teams.map(t => (
                            <button
                              key={t.id}
                              onClick={() => handleQuickTeamAssign(artist.id, t.id)}
                              className={cn(
                                "w-full text-left text-xs px-2 py-1.5 rounded hover:bg-zinc-800 transition-colors flex items-center gap-2",
                                artist.team === t.id ? "bg-zinc-800/50" : ""
                              )}
                              data-testid={`team-option-${t.id}-${artist.id}`}
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: t.color }}
                              />
                              <span style={{ color: artist.team === t.id ? t.color : "#d4d4d8" }}>
                                {t.name}
                              </span>
                            </button>
                          ))}
                          <div className="border-t border-zinc-800 mt-1 pt-1">
                            <button
                              onClick={() => { setShowTeamsDialog(true); }}
                              className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-zinc-800 text-zinc-500 flex items-center gap-2 transition-colors"
                              data-testid={`create-team-from-row-${artist.id}`}
                            >
                              <Plus className="h-3 w-3" />
                              Создать команду...
                            </button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    artist.team ? (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: `${teams.find(t => t.id === artist.team)?.color || "#6b7280"}20`,
                          color: teams.find(t => t.id === artist.team)?.color || "#6b7280",
                          border: `1px solid ${teams.find(t => t.id === artist.team)?.color || "#6b7280"}50`
                        }}
                      >
                        {teams.find(t => t.id === artist.team)?.name || artist.team}
                      </span>
                    ) : (
                      <span className="text-zinc-600 text-xs">—</span>
                    )
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {getRatingStars(artist.rating)}
                </TableCell>
                <TableCell className="text-center">
                  <span className={cn(
                    "font-mono font-medium",
                    artist.assignedNotClosed > 0 ? "text-amber-400" : "text-zinc-600"
                  )}>
                    {artist.assignedNotClosed}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span className={cn(
                    "font-mono font-medium",
                    artist.activeTasks > 0 ? "text-sky-400" : "text-zinc-600"
                  )}>
                    {artist.activeTasks}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span className={cn(
                    "font-mono",
                    artist.completedTasks > 0 ? "text-green-400" : "text-zinc-600"
                  )}>
                    {artist.completedTasks}
                  </span>
                </TableCell>
                <TableCell>
                  {getWorkloadBar(artist.workloadPercent)}
                </TableCell>
                <TableCell className="text-center">
                  {getAvailabilityBadge(artist)}
                </TableCell>
                {canEditArtists && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowArtistTasks(artist);
                        }}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-sky-400 hover:bg-zinc-800"
                        title="View Tasks"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditArtist(artist)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800"
                        title="Edit Artist"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* New/Edit Artist Dialog */}
      <Dialog open={showNewArtist} onOpenChange={(open) => {
        if (!open) resetArtistForm();
        setShowNewArtist(open);
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">{editingArtist ? "Edit Artist" : "Add New Artist"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Full Name *</Label>
                <Input
                  value={newArtist.name}
                  onChange={(e) => setNewArtist({ ...newArtist, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="Enter artist name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Email *</Label>
                <Input
                  type="email"
                  value={newArtist.email}
                  onChange={(e) => setNewArtist({ ...newArtist, email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="artist@email.com"
                />
              </div>
            </div>

            {/* Contact Info Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Phone</Label>
                <Input
                  value={newArtist.phone}
                  onChange={(e) => setNewArtist({ ...newArtist, phone: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="+380..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Telegram</Label>
                <Input
                  value={newArtist.telegram}
                  onChange={(e) => setNewArtist({ ...newArtist, telegram: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="@username"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">{editingArtist ? "New Password" : "Password"}</Label>
                <Input
                  type="password"
                  value={newArtist.password}
                  onChange={(e) => setNewArtist({ ...newArtist, password: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder={editingArtist ? "Leave empty to keep" : "123456"}
                />
              </div>
            </div>

            {/* Rate and Portfolio */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Hourly Rate</Label>
                <Input
                  type="number"
                  min="0"
                  value={newArtist.hourly_rate}
                  onChange={(e) => setNewArtist({ ...newArtist, hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Currency</Label>
                <Select 
                  value={newArtist.currency} 
                  onValueChange={(v) => setNewArtist({ ...newArtist, currency: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="UAH" className="text-zinc-50">UAH</SelectItem>
                    <SelectItem value="USD" className="text-zinc-50">USD</SelectItem>
                    <SelectItem value="EUR" className="text-zinc-50">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Rating (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={newArtist.rating}
                  onChange={(e) => setNewArtist({ ...newArtist, rating: parseInt(e.target.value) || 7 })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="7"
                />
              </div>
            </div>

            {/* Portfolio Link */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Portfolio Link</Label>
              <Input
                value={newArtist.portfolio_link}
                onChange={(e) => setNewArtist({ ...newArtist, portfolio_link: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                placeholder="https://behance.net/..."
              />
            </div>

            {/* Specializations */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Specializations</Label>
              <div className="flex flex-wrap gap-2">
                {SPECIALIZATIONS.map(spec => (
                  <Badge
                    key={spec.value}
                    variant="outline"
                    className={cn(
                      "cursor-pointer transition-all",
                      newArtist.specializations.includes(spec.value)
                        ? "border-orange-500 bg-orange-500/20 text-orange-400"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                    )}
                    onClick={() => {
                      if (newArtist.specializations.includes(spec.value)) {
                        setNewArtist({ ...newArtist, specializations: newArtist.specializations.filter(s => s !== spec.value) });
                      } else {
                        setNewArtist({ ...newArtist, specializations: [...newArtist.specializations, spec.value] });
                      }
                    }}
                  >
                    {spec.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Notes</Label>
              <Textarea
                value={newArtist.notes}
                onChange={(e) => setNewArtist({ ...newArtist, notes: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                placeholder="Additional notes about the artist..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setShowNewArtist(false); resetArtistForm(); }} className="border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleCreateArtist} className="bg-orange-500 hover:bg-orange-600">
              {editingArtist ? "Update Artist" : "Create Artist"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Artist Tasks Dialog */}
      <Dialog open={showTasksDialog} onOpenChange={setShowTasksDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <User className="h-5 w-5 text-orange-400" />
              Таски артиста: {selectedArtistName}
            </DialogTitle>
          </DialogHeader>
          {loadingTasks ? (
            <div className="text-center py-8 text-zinc-500">Завантаження...</div>
          ) : (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setTasksTab("active")}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    tasksTab === "active" 
                      ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" 
                      : "bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                  )}
                >
                  В роботі ({selectedArtistTasks.filter(t => !isTaskDone(t)).length})
                </button>
                <button
                  onClick={() => setTasksTab("completed")}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    tasksTab === "completed" 
                      ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                      : "bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                  )}
                >
                  Завершено ({selectedArtistTasks.filter(t => isTaskDone(t)).length})
                </button>
              </div>

              {/* Where the tab's tasks stand, by stage */}
              {(() => {
                const counts = {};
                selectedArtistTasks
                  .filter(t => (tasksTab === "active") !== isTaskDone(t))
                  .forEach(t => {
                    const st = t.resolved_stage;
                    if (!st) return;
                    counts[st.name] = counts[st.name] || { n: 0, color: st.color };
                    counts[st.name].n += 1;
                  });
                return (
                  <div className="flex flex-wrap gap-2 text-sm">
                    {Object.entries(counts).map(([name, { n, color }]) => (
                      <span key={name} className="px-3 py-1.5 rounded-lg border" style={stageBadgeStyle(color)}>
                        {name}: <span className="font-mono">{n}</span>
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Tasks Table */}
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400 text-xs">Таска</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Проект</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Тип</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Етап</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Оплата</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right">Ціна</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      // Filter tasks based on active tab
                      const filteredTasks = tasksTab === "active"
                        ? selectedArtistTasks.filter(t => !isTaskDone(t))
                        : selectedArtistTasks.filter(t => isTaskDone(t));
                      
                      if (filteredTasks.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                              {tasksTab === "active" ? "Немає активних тасок" : "Немає завершених тасок"}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      
                      return filteredTasks.map((task) => (
                        <TableRow key={task.id} className="border-zinc-800 hover:bg-zinc-800/30">
                          <TableCell className="text-sm">
                            <a 
                              href={`/tasks/${task.id}`}
                              className="text-zinc-50 hover:text-orange-400"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {task.name?.substring(0, 40)}{task.name?.length > 40 ? '...' : ''}
                            </a>
                          </TableCell>
                          <TableCell className="text-xs text-zinc-400">
                            {task.project_name || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-zinc-400">
                            {task.task_type || task.service_type || '—'}
                          </TableCell>
                          <TableCell>
                            <TaskStageBadge task={task} size="sm" />
                          </TableCell>
                          <TableCell>
                            <Badge className={cn(
                              "text-[10px] h-5",
                              task.payment_status === 'paid' ? "bg-green-500/20 text-green-400 border-green-500/30" :
                              task.payment_status === 'approved' ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                              "bg-zinc-700/50 text-zinc-400 border-zinc-600/30"
                            )}>
                              {task.payment_status === 'paid' ? 'Оплачено' : 
                               task.payment_status === 'approved' ? 'До оплати' : 'Очікує'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-zinc-50">
                            {task.currency === 'UAH' ? '₴' : '$'}{task.agreed_price?.toLocaleString() || '0'}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Teams Management Dialog */}
      <Dialog open={showTeamsDialog} onOpenChange={setShowTeamsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md" data-testid="teams-management-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-400" />
              Управление командами
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Create new team */}
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1.5">
                <Label className="text-zinc-400 text-xs">Новая команда</Label>
                <Input
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Название команды..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9"
                  data-testid="new-team-name-input"
                  onKeyDown={(e) => e.key === "Enter" && handleCreateTeam()}
                />
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="h-9 w-9 rounded-md border border-zinc-700 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: newTeamColor }}
                    data-testid="team-color-picker"
                  />
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2 bg-zinc-900 border-zinc-700" align="end">
                  <div className="grid grid-cols-5 gap-1.5">
                    {TEAM_COLORS.map(c => (
                      <button
                        key={c}
                        onClick={() => setNewTeamColor(c)}
                        className={cn(
                          "w-7 h-7 rounded-md border-2 transition-all",
                          newTeamColor === c ? "border-white scale-110" : "border-transparent hover:border-zinc-500"
                        )}
                        style={{ backgroundColor: c }}
                        data-testid={`team-color-${c}`}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                size="sm"
                onClick={handleCreateTeam}
                disabled={!newTeamName.trim() || creatingTeam}
                className="bg-orange-500 hover:bg-orange-600 h-9 px-3"
                data-testid="create-team-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Existing teams */}
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Существующие команды</Label>
              {teams.length === 0 ? (
                <p className="text-zinc-600 text-sm py-4 text-center">Нет команд</p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {teams.map(t => {
                    const memberCount = artists.filter(a => a.team === t.id).length;
                    return (
                      <div
                        key={t.id}
                        className="flex items-center justify-between px-3 py-2 bg-zinc-800/50 rounded-lg group"
                        data-testid={`team-item-${t.id}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: t.color }}
                          />
                          <span className="text-sm text-zinc-50 font-medium">{t.name}</span>
                          <span className="text-xs text-zinc-500 font-mono">
                            {memberCount} {memberCount === 1 ? 'артист' : 'артистов'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTeam(t.id, t.name)}
                          className="h-7 w-7 p-0 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-team-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistsPage;
